import type { Tab } from './index';
import { esc } from '../utils';
import { alpacaFetch, fetchAlpacaSnapshot } from '../alpaca';
import { buildGauges, type Bar } from './gauges';
import { buildPatterns } from './patterns';
import { showToast } from '../toast';

// ── TradingView symbol resolution ─────────────────────────────────────────────

const TV_SYMBOL_MAP: Record<string, string> = {
  // Tracked tickers
  HAWK:'NYSE:HAWK', PL:'NYSE:PL', SATL:'NASDAQ:SATL', IONQ:'NYSE:IONQ',
  KRMN:'NYSE:KRMN', VOYG:'NYSE:VOYG', FLY:'NASDAQ:FLY', MDA:'NYSE:MDA',
  XNDU:'NASDAQ:XNDU', INFQ:'NYSE:INFQ', RXRX:'NASDAQ:RXRX', TEM:'NASDAQ:TEM',
  SDGR:'NASDAQ:SDGR', CRSP:'NASDAQ:CRSP', NTLA:'NASDAQ:NTLA', BEAM:'NASDAQ:BEAM',
  ILMN:'NASDAQ:ILMN', GENB:'NASDAQ:GENB', KLRA:'NASDAQ:KLRA', CBRS:'NASDAQ:CBRS',
  // ETFs / Indices
  SPY:'AMEX:SPY', QQQ:'NASDAQ:QQQ', IWM:'AMEX:IWM', DIA:'AMEX:DIA',
  VTI:'AMEX:VTI', VOO:'AMEX:VOO', GLD:'AMEX:GLD', SLV:'AMEX:SLV',
  TLT:'NASDAQ:TLT', HYG:'AMEX:HYG', XLF:'AMEX:XLF', XLE:'AMEX:XLE',
  XLK:'AMEX:XLK', XBI:'AMEX:XBI', ARKK:'AMEX:ARKK',
  SPX:'SP:SPX', NDX:'NASDAQ:NDX', DJI:'DJ:DJI', RUT:'TVC:RUT', VIX:'CBOE:VIX',
  // Popular stocks
  NVDA:'NASDAQ:NVDA', AAPL:'NASDAQ:AAPL', MSFT:'NASDAQ:MSFT', GOOGL:'NASDAQ:GOOGL',
  AMZN:'NASDAQ:AMZN', META:'NASDAQ:META', TSLA:'NASDAQ:TSLA', AMD:'NASDAQ:AMD',
  INTC:'NASDAQ:INTC', PLTR:'NYSE:PLTR', RKLB:'NASDAQ:RKLB', COIN:'NASDAQ:COIN',
  HOOD:'NASDAQ:HOOD', SOFI:'NASDAQ:SOFI', GS:'NYSE:GS', JPM:'NYSE:JPM',
  DJT:'NASDAQ:DJT', NVAX:'NASDAQ:NVAX', MRNA:'NASDAQ:MRNA',
  // Crypto
  BTC:'COINBASE:BTCUSD', BTCUSD:'COINBASE:BTCUSD', 'BTC-USD':'COINBASE:BTCUSD',
  ETH:'COINBASE:ETHUSD', ETHUSD:'COINBASE:ETHUSD', 'ETH-USD':'COINBASE:ETHUSD',
  SOL:'COINBASE:SOLUSD', SOLUSD:'COINBASE:SOLUSD', 'SOL-USD':'COINBASE:SOLUSD',
  BNB:'BINANCE:BNBUSD', XRP:'COINBASE:XRPUSD', DOGE:'COINBASE:DOGEUSD',
  ADA:'COINBASE:ADAUSD', AVAX:'COINBASE:AVAXUSD', LINK:'COINBASE:LINKUSD',
  // Commodities / Forex / Indices
  GOLD:'TVC:GOLD', SILVER:'TVC:SILVER', OIL:'TVC:USOIL',
  EURUSD:'FX:EURUSD', GBPUSD:'FX:GBPUSD', USDJPY:'FX:USDJPY',
  FTSE:'TVC:UKX', DAX:'XETR:DAX', NIKKEI:'TVC:NI225',
  US10Y:'TVC:US10Y', US2Y:'TVC:US02Y', US30Y:'TVC:US30Y',
};

const CRYPTO_TICKERS = new Set([
  'BTC','ETH','SOL','BNB','XRP','DOGE','ADA','AVAX','LINK','DOT','MATIC','UNI',
  'SHIB','LTC','BTCUSD','ETHUSD','SOLUSD','BTC-USD','ETH-USD','SOL-USD',
]);

const CHART_EXCHANGES: Record<string, string> = {
  HAWK:'NYSE', PL:'NYSE', SATL:'NASDAQ', IONQ:'NYSE', KRMN:'NYSE', VOYG:'NYSE',
  FLY:'NASDAQ', MDA:'NYSE', XNDU:'NASDAQ', INFQ:'NYSE', RXRX:'NASDAQ', TEM:'NASDAQ',
  SDGR:'NASDAQ', CRSP:'NASDAQ', NTLA:'NASDAQ', BEAM:'NASDAQ', ILMN:'NASDAQ',
  GENB:'NASDAQ', KLRA:'NASDAQ', CBRS:'NASDAQ',
};

const TF_MAP: Record<string, { interval: string; range: string }> = {
  '1D':{ interval:'5',   range:'1D'  },
  '1W':{ interval:'60',  range:'5D'  },
  '1M':{ interval:'D',   range:'1M'  },
  '3M':{ interval:'D',   range:'3M'  },
  '6M':{ interval:'D',   range:'6M'  },
  '1Y':{ interval:'W',   range:'12M' },
  '5Y':{ interval:'W',   range:'60M' },
};

// ── Tab implementation ─────────────────────────────────────────────────────────

export class ChartsTab implements Tab {
  readonly id = 'charts';

  private ticker: string | null = null;
  private tf = '3M';
  private orderSide: 'buy' | 'sell' = 'buy';
  private currentQuote: number | null = null;
  private gaugeTf = 'D';                              // divergence gauge timeframe
  private gaugeBars = new Map<string, Bar[]>();       // session cache: ticker|tf

  private get alpKey()    { return localStorage.getItem('meridian_alpaca_key')    ?? ''; }
  private get alpSecret() { return localStorage.getItem('meridian_alpaca_secret') ?? ''; }

  private el(id: string): HTMLElement { return document.getElementById(id) as HTMLElement; }

  init(): void {
    // Sub-tab nav: Chart | Scanner | Signals
    this.el('charts-sub-chart').addEventListener('click', () => {
      (window as any).showTab?.('charts');
    });
    this.el('charts-sub-scanner').addEventListener('click', () => {
      (window as any).showTab?.('scanner');
    });
    this.el('charts-sub-signals').addEventListener('click', () => {
      (window as any).showTab?.('signals');
    });

    // Load button + Enter key
    this.el('chart-load-btn').addEventListener('click', () => {
      const t = (this.el('chart-custom-ticker') as HTMLInputElement).value.trim().toUpperCase();
      if (t) this.loadTicker(t);
    });
    (this.el('chart-custom-ticker') as HTMLInputElement)
      .addEventListener('keydown', e => { if (e.key === 'Enter') { const t = (e.target as HTMLInputElement).value.trim().toUpperCase(); if (t) this.loadTicker(t); } });

    // Indicator checkboxes
    document.querySelectorAll<HTMLInputElement>('#tab-charts input[type=checkbox]').forEach(cb =>
      cb.addEventListener('change', () => this.reloadIndicators()),
    );

    // Gauge timeframe pills — divergence recomputes; momentum/RVOL stay daily.
    // (The TV embed's internal timeframe is sealed in its iframe, so the
    // cluster carries its own selector — gauges-handoff premise adapted.)
    this.el('chart-gauge-tf')?.addEventListener('click', e => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-gtf]');
      if (!btn) return;
      this.el('chart-gauge-tf').querySelectorAll('button').forEach(b => {
        const on = b === btn;
        b.classList.toggle('active', on);
        b.style.borderColor = on ? 'var(--accent)' : 'var(--border)';
        b.style.color = on ? 'var(--accent)' : 'var(--text3)';
      });
      this.gaugeTf = btn.dataset.gtf!;
      if (this.ticker) void this.loadGauges(this.ticker);
    });

    // Preset buttons
    document.querySelectorAll<HTMLElement>('[data-preset]').forEach(btn =>
      btn.addEventListener('click', () => this.applyPreset(btn.dataset.preset ?? 'clean')),
    );

    // CT panel: side buttons
    this.el('ct-side-buy').addEventListener('click',  () => { this.setOrderSide('buy');  this.setCTSide('buy');  });
    this.el('ct-side-sell').addEventListener('click', () => { this.setOrderSide('sell'); this.setCTSide('sell'); });

    // CT panel: inputs sync
    (this.el('ct-symbol') as HTMLInputElement).addEventListener('input', e => {
      const sym = (e.target as HTMLInputElement).value.toUpperCase();
      (e.target as HTMLInputElement).value = sym;
      this.fetchQuote(sym);
    });
    (this.el('ct-qty') as HTMLInputElement).addEventListener('input', () => this.updateTotal());
    (this.el('ct-type') as HTMLSelectElement).addEventListener('change', () => this.syncCTTypeFields());
    (this.el('ct-limit') as HTMLInputElement).addEventListener('input', () => this.updateTotal());

    // CT panel: submit
    this.el('ct-submit-btn').addEventListener('click', () => { this.syncCTToMain(); this.confirmOrder(); });

    // CT panel: refresh
    document.querySelector<HTMLElement>('#tab-charts [data-action="refresh-ct"]')
      ?.addEventListener('click', () => this.refreshCT());

    // Alpaca settings — now delegates to the global settings panel
    document.querySelector<HTMLElement>('[onclick="openSettings()"]')
      ?.addEventListener('click', () => (window as any).openSettings?.());

    // Expose for cross-tab calls from Holdings tab stubs
    (window as any).loadChartTicker        = (t: string) => this.loadTicker(t);
    (window as any).quickTrade             = (sym: string, side: 'buy' | 'sell') => this.quickTrade(sym, side);
    (window as any).loadOptionsFromTracker = (t: string) => this.loadOptions(t);
  }

  render(): void {
    if (this.alpKey && this.alpSecret) this.refreshCT();
    if (!this.ticker) this.loadTicker('AAPL'); // default chart on first open
  }

  // ── Public cross-tab API ──────────────────────────────────────────────────

  loadTicker(ticker: string): void {
    if (!ticker) return;
    ticker = ticker.toUpperCase().trim();
    this.ticker = ticker;

    const tvSymbol = this.resolveTVSymbol(ticker);
    const assetType = this.getAssetType(ticker);

    // Switch to charts tab
    (window as any).showTab?.('charts');

    this.el('chart-ticker-label').textContent  = ticker;
    this.el('chart-company-label').textContent = `${assetType} · ${tvSymbol}`;
    this.el('chart-empty').style.display       = 'none';
    this.el('chart-error').style.display       = 'none';
    this.el('chart-loading').style.display     = 'block';

    const { interval, range } = TF_MAP[this.tf] ?? TF_MAP['3M'];
    const isCrypto = CRYPTO_TICKERS.has(ticker) || ticker.endsWith('USD') || ticker.includes('-USD');

    const studies: { id: string; inputs?: Record<string, unknown> }[] = [];
    const cb = (id: string) => (document.getElementById(id) as HTMLInputElement | null)?.checked;
    if (cb('show-ma50')  && !isCrypto) studies.push({ id:'MASimple@tv-basicstudies', inputs:{ length:50 } });
    if (cb('show-ma200') && !isCrypto) studies.push({ id:'MASimple@tv-basicstudies', inputs:{ length:200 } });
    if (cb('show-ema20'))  studies.push({ id:'MAExp@tv-basicstudies', inputs:{ length:20 } });
    if (cb('show-bb'))     studies.push({ id:'BB@tv-basicstudies', inputs:{ length:20, mult:2 } });
    if (cb('show-vwap'))   studies.push({ id:'VWAP@tv-basicstudies' });
    if (cb('show-rsi'))    studies.push({ id:'RSI@tv-basicstudies', inputs:{ length:14 } });
    if (cb('show-macd'))   studies.push({ id:'MACD@tv-basicstudies', inputs:{ fast_length:12, slow_length:26, signal_smoothing:9 } });
    if (cb('show-stoch'))  studies.push({ id:'Stochastic@tv-basicstudies', inputs:{ smoothK:3, smoothD:3, lengthRSI:14, lengthStoch:14 } });
    if (cb('show-atr'))    studies.push({ id:'ATR@tv-basicstudies', inputs:{ length:14 } });
    if (cb('show-obv'))    studies.push({ id:'OBV@tv-basicstudies' });

    const container = this.el('chart-widget-container');
    container.innerHTML = '';
    container.style.display = 'block';

    const wDiv = document.createElement('div');
    wDiv.className = 'tradingview-widget-container';
    wDiv.style.cssText = 'height:520px;width:100%;';
    const inner = document.createElement('div');
    inner.id = 'tradingview_chart';
    inner.style.cssText = 'height:100%;width:100%;';
    wDiv.appendChild(inner);
    container.appendChild(wDiv);

    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/tv.js';
    script.onload = () => {
      this.el('chart-loading').style.display = 'none';
      new (window as any).TradingView.widget({
        autosize: true, symbol: tvSymbol, interval, range,
        timezone: 'America/New_York', theme: 'dark', style: '1', locale: 'en',
        toolbar_bg: '#0a1118', enable_publishing: false, hide_side_toolbar: false,
        allow_symbol_change: true, container_id: 'tradingview_chart',
        backgroundColor: '#050a0e', gridColor: 'rgba(30,52,72,0.4)',
        studies, show_popup_button: true, popup_width: '1000', popup_height: '650',
        withdateranges: true, details: true, hotlist: false, calendar: false,
        hide_volume: !cb('show-volume'),
      });
      this.syncCTSymbol(ticker);
    };
    void this.loadGauges(ticker);
    script.onerror = () => {
      this.el('chart-loading').style.display = 'none';
      const errEl = this.el('chart-error');
      errEl.style.display = 'block';
      errEl.textContent = `Could not load TradingView chart for ${ticker}. Check your internet connection.`;
    };
    document.head.appendChild(script);
  }

  quickTrade(symbol: string, side: 'buy' | 'sell'): void {
    (window as any).showTab?.('charts');
    (this.el('ct-symbol') as HTMLInputElement).value = symbol;
    this.setOrderSide(side); this.setCTSide(side);
    this.syncCTSymbol(symbol);
    (this.el('ct-qty') as HTMLInputElement).focus();
    this.refreshCT();
  }

  loadOptions(_ticker: string): void {
    (window as any).showTab?.('optionschain');
    // Wire to options tab when migrated
    showToast('Options tab not yet migrated', 'warn');
  }

  // ── Gauge cluster (gauges-handoff.md) ─────────────────────────────────────
  // Fetches its own Alpaca bars (the TV embed exposes nothing): all three gauges
  // (momentum, RVOL, divergence) compute on the selected gauge timeframe (gaugeTf).

  private async fetchGaugeBars(ticker: string, gtf: string): Promise<Bar[]> {
    const key = `${ticker}|${gtf}`;
    const hit = this.gaugeBars.get(key);
    if (hit) return hit;
    // start is required: without it Alpaca defaults to TODAY and limit is
    // meaningless (you get one bar). daysBack sized so limit is the binding cap.
    const tfMap: Record<string, { tf: string; limit: number; daysBack: number }> = {
      '5m': { tf: '5Min',  limit: 200, daysBack: 7 },
      '1H': { tf: '1Hour', limit: 200, daysBack: 30 },
      'D':  { tf: '1Day',  limit: 60,  daysBack: 120 },
      'W':  { tf: '1Week', limit: 60,  daysBack: 500 },
    };
    const { tf, limit, daysBack } = tfMap[gtf] ?? tfMap['D'];
    const start = new Date(Date.now() - daysBack * 86400e3).toISOString().slice(0, 10);
    const resp = await alpacaFetch<{ bars: { t: string; o: number; h: number; l: number; c: number; v: number }[] }>(
      `/v2/stocks/${ticker}/bars?timeframe=${tf}&start=${start}&limit=${limit}&feed=iex&adjustment=split&sort=desc`, 'GET', null, true,
    );
    const bars = (resp.bars ?? []).slice().reverse();   // oldest-first for the math
    this.gaugeBars.set(key, bars);
    return bars;
  }

  private async loadGauges(ticker: string): Promise<void> {
    const wrap = this.el('chart-gauges-wrap');
    if (!wrap) return;
    if (!this.alpKey || !this.alpSecret) { wrap.style.display = 'none'; return; }
    try {
      const bars = await this.fetchGaugeBars(ticker, this.gaugeTf);
      // No Alpaca data (delisted/crypto): hide, don't error.
      if (!bars.length) { wrap.style.display = 'none'; return; }
      this.el('chart-gauges').innerHTML = buildGauges(bars, this.gaugeTf);
      const pe = this.el('chart-patterns');
      if (pe) pe.innerHTML = buildPatterns(bars, this.gaugeTf);
      wrap.style.display = 'block';
    } catch {
      wrap.style.display = 'none';
    }
  }

  // ── Indicators ────────────────────────────────────────────────────────────

  private reloadIndicators(): void { if (this.ticker) this.loadTicker(this.ticker); }

  private applyPreset(preset: string): void {
    const on  = (id: string) => { const el = document.getElementById(id) as HTMLInputElement | null; if (el) el.checked = true;  };
    const off = (id: string) => { const el = document.getElementById(id) as HTMLInputElement | null; if (el) el.checked = false; };
    ['show-ma50','show-ma200','show-ema20','show-bb','show-vwap',
     'show-rsi','show-macd','show-stoch','show-atr','show-obv','show-volume'].forEach(off);
    if (preset === 'scalp')    { on('show-rsi'); on('show-macd'); on('show-ema20'); on('show-volume'); }
    if (preset === 'swing')    { on('show-ma50'); on('show-ma200'); on('show-bb'); on('show-rsi'); on('show-volume'); }
    if (preset === 'momentum') { on('show-macd'); on('show-stoch'); on('show-obv'); }
    if (preset === 'clean')    { /* all off */ }
    this.reloadIndicators();
  }

  // ── CT panel (compact trading) ─────────────────────────────────────────────

  private async refreshCT(): Promise<void> {
    if (!this.alpKey || !this.alpSecret) return;
    try {
      const [positions, orders] = await Promise.all([
        alpacaFetch<any[]>('/v2/positions'),
        alpacaFetch<any[]>('/v2/orders?status=open&limit=20'),
      ]);
      this.renderCTPositions(positions ?? []);
      this.renderCTOrders(orders ?? []);
    } catch { /* silent — keys may not be set */ }
  }

  private renderCTPositions(positions: any[]): void {
    const empty = this.el('ct-positions-empty');
    const wrap  = this.el('ct-positions-wrap');
    const tbody = this.el('ct-positions-body');
    if (!positions.length) { empty.style.display = 'block'; wrap.style.display = 'none'; return; }
    empty.style.display = 'none'; wrap.style.display = 'block';
    tbody.innerHTML = positions.map(p => {
      const pl  = parseFloat(p.unrealized_pl ?? '0');
      const plp = parseFloat(p.unrealized_plpc ?? '0') * 100;
      const col = pl >= 0 ? 'var(--green)' : 'var(--red)';
      return `<tr>
        <td style="font-weight:700;color:var(--accent);">${esc(p.symbol)}</td>
        <td>${esc(p.qty)}</td>
        <td>$${parseFloat(p.avg_entry_price ?? '0').toFixed(2)}</td>
        <td>$${parseFloat(p.current_price ?? '0').toFixed(2)}</td>
        <td style="color:${col};">${pl >= 0 ? '+' : ''}$${pl.toFixed(2)}</td>
        <td style="color:${col};">${plp >= 0 ? '+' : ''}${plp.toFixed(2)}%</td>
        <td><button data-sym="${esc(p.symbol)}" data-action="close-pos" style="background:rgba(255,71,87,0.08);border:1px solid rgba(255,71,87,0.2);color:var(--red);font-family:var(--mono);font-size:10px;padding:2px 6px;cursor:pointer;border-radius:2px;">✕</button></td>
      </tr>`;
    }).join('');
    tbody.querySelectorAll<HTMLElement>('[data-action="close-pos"]').forEach(btn =>
      btn.addEventListener('click', () => this.closePos(btn.dataset.sym ?? '')),
    );
  }

  private renderCTOrders(orders: any[]): void {
    const empty = this.el('ct-orders-empty');
    const wrap  = this.el('ct-orders-wrap');
    const tbody = this.el('ct-orders-body');
    if (!orders.length) { empty.style.display = 'block'; wrap.style.display = 'none'; return; }
    empty.style.display = 'none'; wrap.style.display = 'block';
    tbody.innerHTML = orders.map(o => `<tr>
      <td style="font-weight:700;">${esc(o.symbol)}</td>
      <td style="color:${o.side==='buy'?'var(--green)':'var(--red)'};">${esc(o.side?.toUpperCase())}</td>
      <td>${esc(o.type)}</td>
      <td>${esc(o.qty)}</td>
      <td>${o.limit_price ? '$'+parseFloat(o.limit_price).toFixed(2) : '—'}</td>
      <td style="color:var(--text3);">${esc(o.status)}</td>
      <td><button data-id="${esc(o.id)}" data-action="cancel-ord" style="background:rgba(255,71,87,0.08);border:1px solid rgba(255,71,87,0.2);color:var(--red);font-family:var(--mono);font-size:10px;padding:2px 6px;cursor:pointer;border-radius:2px;">✕</button></td>
    </tr>`).join('');
    tbody.querySelectorAll<HTMLElement>('[data-action="cancel-ord"]').forEach(btn =>
      btn.addEventListener('click', () => this.cancelOrder(btn.dataset.id ?? '')),
    );
  }

  private setOrderSide(side: 'buy' | 'sell'): void {
    this.orderSide = side;
    // Mirror to main trading panel if it exists
    const buyBtn  = document.getElementById('alp-side-buy');
    const sellBtn = document.getElementById('alp-side-sell');
    if (side === 'buy') {
      if (buyBtn)  { buyBtn.style.background='rgba(46,213,115,0.15)';  buyBtn.style.borderColor='rgba(46,213,115,0.4)';  buyBtn.style.color='var(--green)'; }
      if (sellBtn) { sellBtn.style.background='transparent'; sellBtn.style.borderColor='var(--border)'; sellBtn.style.color='var(--text3)'; }
    } else {
      if (sellBtn) { sellBtn.style.background='rgba(255,71,87,0.15)'; sellBtn.style.borderColor='rgba(255,71,87,0.4)'; sellBtn.style.color='var(--red)'; }
      if (buyBtn)  { buyBtn.style.background='transparent'; buyBtn.style.borderColor='var(--border)'; buyBtn.style.color='var(--text3)'; }
    }
    this.updateTotal();
  }

  private setCTSide(side: 'buy' | 'sell'): void {
    const buy    = this.el('ct-side-buy');
    const sell   = this.el('ct-side-sell');
    const submit = this.el('ct-submit-btn');
    if (side === 'buy') {
      buy.style.background='rgba(46,213,115,0.15)'; buy.style.borderColor='rgba(46,213,115,0.4)'; buy.style.color='var(--green)';
      sell.style.background='transparent'; sell.style.borderColor='var(--border)'; sell.style.color='var(--text3)';
      submit.style.color='var(--green)'; submit.style.borderColor='rgba(46,213,115,0.35)'; submit.style.background='rgba(46,213,115,0.12)';
    } else {
      sell.style.background='rgba(255,71,87,0.15)'; sell.style.borderColor='rgba(255,71,87,0.4)'; sell.style.color='var(--red)';
      buy.style.background='transparent'; buy.style.borderColor='var(--border)'; buy.style.color='var(--text3)';
      submit.style.color='var(--red)'; submit.style.borderColor='rgba(255,71,87,0.35)'; submit.style.background='rgba(255,71,87,0.12)';
    }
  }

  private syncCTSymbol(ticker: string): void {
    (this.el('ct-symbol') as HTMLInputElement).value = ticker;
    fetchAlpacaSnapshot(ticker).then(data => {
      if (!data) return;
      this.currentQuote = data.price;
      const quoteEl = this.el('ct-quote');
      const up = data.change >= 0;
      quoteEl.innerHTML = `<span style="color:var(--accent);font-weight:700;">$${data.price.toFixed(2)}</span> <span style="color:${up?'var(--green)':'var(--red)'};">${up?'+':''}${data.changePct.toFixed(2)}%</span>`;
      this.updateTotal();
    }).catch(() => {});
  }

  private async fetchQuote(symbol: string): Promise<void> {
    if (!symbol) return;
    const quoteEl = this.el('ct-quote');
    quoteEl.textContent = 'Fetching…';
    try {
      const data = await fetchAlpacaSnapshot(symbol);
      if (!data) { quoteEl.textContent = 'N/A'; return; }
      this.currentQuote = data.price;
      const up = data.change >= 0;
      quoteEl.innerHTML = `<span style="color:var(--accent);font-weight:700;">$${data.price.toFixed(2)}</span> <span style="color:${up?'var(--green)':'var(--red)'};">${up?'+':''}${data.changePct.toFixed(2)}%</span>`;
      this.updateTotal();
    } catch { quoteEl.textContent = 'Quote error'; }
  }

  private syncCTToMain(): void {
    const sym   = (this.el('ct-symbol') as HTMLInputElement).value;
    const qty   = (this.el('ct-qty')    as HTMLInputElement).value;
    const type  = (this.el('ct-type')   as HTMLSelectElement).value;
    const limit = (this.el('ct-limit')  as HTMLInputElement).value;
    const stop  = (this.el('ct-stop')   as HTMLInputElement).value;
    const setVal = (id: string, v: string) => { const el = document.getElementById(id) as HTMLInputElement | null; if (el) el.value = v; };
    if (sym)   setVal('alp-order-symbol', sym);
    if (qty)   setVal('alp-order-qty', qty);
    if (type)  setVal('alp-order-type', type);
    if (limit) setVal('alp-order-limit', limit);
    if (stop)  setVal('alp-order-stop', stop);
  }

  private syncCTTypeFields(): void {
    const type = (this.el('ct-type') as HTMLSelectElement).value;
    this.el('ct-limit-row').style.display = (type === 'limit' || type === 'stop_limit') ? 'block' : 'none';
    this.el('ct-stop-row').style.display  = (type === 'stop'  || type === 'stop_limit') ? 'block' : 'none';
  }

  private updateTotal(): void {
    const qty     = parseFloat((this.el('ct-qty') as HTMLInputElement).value || '0');
    const totalEl = this.el('ct-total');
    if (this.currentQuote && qty > 0) {
      const est = this.currentQuote * qty;
      totalEl.innerHTML = `Est. <strong style="color:var(--accent);">$${est.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</strong> · ${qty} × $${this.currentQuote.toFixed(2)}`;
    } else {
      totalEl.textContent = 'Enter symbol + shares';
    }
  }

  // ── Order flow ────────────────────────────────────────────────────────────

  private confirmOrder(): void {
    const symbol = (this.el('ct-symbol') as HTMLInputElement).value.trim().toUpperCase();
    const qty    = (this.el('ct-qty')    as HTMLInputElement).value;
    const type   = (this.el('ct-type')   as HTMLSelectElement).value;
    const limit  = (this.el('ct-limit')  as HTMLInputElement).value;
    const stop   = (this.el('ct-stop')   as HTMLInputElement).value;
    if (!symbol || !qty) { showToast('Enter symbol and quantity', 'warn'); return; }

    const order: Record<string, unknown> = { symbol, qty: parseInt(qty), side: this.orderSide, type, time_in_force: 'day' };
    if (limit && (type === 'limit' || type === 'stop_limit')) order['limit_price'] = parseFloat(limit);
    if (stop  && (type === 'stop'  || type === 'stop_limit')) order['stop_price']  = parseFloat(stop);

    const est = this.currentQuote ? ` (~$${(this.currentQuote * parseInt(qty)).toLocaleString()})` : '';
    const col = this.orderSide === 'buy' ? 'var(--green)' : 'var(--red)';

    const modal = document.createElement('div');
    modal.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.8);z-index:10000;display:flex;align-items:center;justify-content:center;padding:20px;';
    modal.innerHTML = `<div style="background:var(--bg2);border:1px solid var(--border);border-radius:6px;padding:28px;max-width:380px;text-align:center;">
      <div style="font-size:32px;margin-bottom:12px;">📋</div>
      <div style="font-family:var(--sans);font-size:18px;font-weight:800;color:${col};margin-bottom:16px;">${this.orderSide.toUpperCase()} ${qty} ${symbol}</div>
      <div style="font-size:12px;color:var(--text2);line-height:2;margin-bottom:20px;background:var(--panel);border:1px solid var(--border);border-radius:3px;padding:12px;">
        <div>Type: <strong>${type.replace('_',' ').toUpperCase()}</strong></div>
        <div>Side: <strong style="color:${col};">${this.orderSide.toUpperCase()}</strong></div>
        <div>Qty: <strong>${qty} shares</strong></div>
        ${limit ? `<div>Limit: <strong>$${limit}</strong></div>` : ''}
        ${stop  ? `<div>Stop: <strong>$${stop}</strong></div>`   : ''}
        <div style="color:var(--accent);margin-top:4px;">Est. value: <strong>${est || 'Market price'}</strong></div>
      </div>
      <div style="background:rgba(255,184,48,0.08);border:1px solid rgba(255,184,48,0.2);border-radius:3px;padding:8px;font-size:11px;color:var(--warn);margin-bottom:16px;">📋 PAPER TRADE — No real money</div>
      <div style="display:flex;gap:8px;justify-content:center;">
        <button id="_modal-cancel" style="background:none;border:1px solid var(--border);color:var(--text2);font-family:var(--mono);font-size:11px;padding:8px 20px;cursor:pointer;border-radius:3px;">CANCEL</button>
        <button id="_modal-confirm" style="background:rgba(46,213,115,0.1);border:1px solid rgba(46,213,115,0.3);color:${col};font-family:var(--mono);font-size:12px;font-weight:700;padding:8px 24px;cursor:pointer;border-radius:3px;letter-spacing:0.1em;">CONFIRM</button>
      </div>
    </div>`;
    document.body.appendChild(modal);
    modal.addEventListener('click', e => { if (e.target === modal) modal.remove(); });
    modal.querySelector('#_modal-cancel')!.addEventListener('click',  () => modal.remove());
    modal.querySelector('#_modal-confirm')!.addEventListener('click', () => { modal.remove(); this.placeOrder(order); });
  }

  private async placeOrder(order: Record<string, unknown>): Promise<void> {
    try {
      await alpacaFetch('/v2/orders', 'POST', order);
      showToast(`✓ Order placed: ${String(order['side']).toUpperCase()} ${order['qty']} ${order['symbol']}`);
      (this.el('ct-qty') as HTMLInputElement).value = '';
      this.currentQuote = null;
      this.updateTotal();
      setTimeout(() => this.refreshCT(), 1000);
    } catch (err: unknown) {
      showToast(`Order failed: ${err instanceof Error ? err.message : String(err)}`, 'error');
    }
  }

  private async cancelOrder(orderId: string): Promise<void> {
    try {
      await alpacaFetch(`/v2/orders/${orderId}`, 'DELETE');
      showToast('✓ Order cancelled');
      setTimeout(() => this.refreshCT(), 800);
    } catch (err: unknown) {
      showToast(`Cancel failed: ${err instanceof Error ? err.message : String(err)}`, 'error');
    }
  }

  private closePos(symbol: string): void {
    const modal = document.createElement('div');
    modal.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.75);z-index:10000;display:flex;align-items:center;justify-content:center;';
    modal.innerHTML = `<div style="background:var(--bg2);border:1px solid var(--border);border-radius:6px;padding:24px;max-width:340px;text-align:center;">
      <div style="font-size:28px;margin-bottom:10px;">⚠️</div>
      <div style="color:var(--text);font-size:13px;margin-bottom:18px;line-height:1.6;">Close entire <strong style="color:var(--red);">${esc(symbol)}</strong> position at market price?</div>
      <div style="display:flex;gap:8px;justify-content:center;">
        <button id="_cp-cancel"  style="background:none;border:1px solid var(--border);color:var(--text2);font-family:var(--mono);font-size:11px;padding:7px 16px;cursor:pointer;border-radius:3px;">CANCEL</button>
        <button id="_cp-confirm" style="background:rgba(255,71,87,0.1);border:1px solid rgba(255,71,87,0.3);color:var(--red);font-family:var(--mono);font-size:11px;padding:7px 16px;cursor:pointer;border-radius:3px;font-weight:700;">CLOSE POSITION</button>
      </div>
    </div>`;
    document.body.appendChild(modal);
    modal.addEventListener('click', e => { if (e.target === modal) modal.remove(); });
    modal.querySelector('#_cp-cancel')!.addEventListener('click',  () => modal.remove());
    modal.querySelector('#_cp-confirm')!.addEventListener('click', () => {
      modal.remove();
      alpacaFetch(`/v2/positions/${symbol}`, 'DELETE')
        .then(() => { showToast(`✓ ${symbol} position closed`); setTimeout(() => this.refreshCT(), 800); })
        .catch((err: unknown) => showToast(`Close failed: ${err instanceof Error ? err.message : String(err)}`, 'error'));
    });
  }

  // ── Symbol resolution ─────────────────────────────────────────────────────

  private resolveTVSymbol(ticker: string): string {
    const t = ticker.toUpperCase().trim();
    if (TV_SYMBOL_MAP[t]) return TV_SYMBOL_MAP[t];
    if (CRYPTO_TICKERS.has(t) || t.endsWith('USD') || t.endsWith('-USD'))
      return `COINBASE:${t.replace('-','').replace('USD','')}USD`;
    const ex = CHART_EXCHANGES[t];
    if (ex) return `${ex}:${t}`;
    return t;
  }

  private getAssetType(ticker: string): string {
    const t = ticker.toUpperCase();
    if (CRYPTO_TICKERS.has(t) || t.endsWith('USD') || t.includes('-USD')) return 'Crypto';
    if (['SPX','NDX','DJI','RUT','VIX','FTSE','DAX','NIKKEI'].includes(t)) return 'Index';
    if (['EURUSD','GBPUSD','USDJPY','AUDUSD','USDCAD'].includes(t))        return 'Forex';
    if (['GOLD','SILVER','OIL','NATGAS'].includes(t))                       return 'Commodity';
    return 'Stock';
  }
}
